import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hubIdFromUrl = searchParams.get('hubId');
  const { user } = useAuth();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [error, setError] = useState(null);
  const [showGrowerInfo, setShowGrowerInfo] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const url = hubIdFromUrl ? `/api/products/${id}?hubId=${hubIdFromUrl}` : `/api/products/${id}`;
        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (!response.ok) throw new Error('Product not found');
        const data = await response.json();
        setProduct(data);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id, hubIdFromUrl]);

  const handleAddToCart = () => {
    let source = null;
    let sourceName = '';
    
    if (hubIdFromUrl) {
      // Product viewed through hub
      source = { type: 'hub', id: hubIdFromUrl };
      sourceName = product?.hubName || 'Hub';
    } else if (product?.isHubDirect) {
      source = { type: 'direct', id: product.hubId };
      sourceName = 'Hub Direct';
    } else {
      source = { type: 'direct', id: product?.producer?._id || product?.producer };
      sourceName = product?.producer?.farmName || product?.producer?.name || 'Farm';
    }
    
    addToCart(product, quantity, source, sourceName);
    navigate('/cart');
  };

  const isSeller = () => {
    if (!user) return false;
    const sellerId = hubIdFromUrl || product?.hubId || product?.producer?._id || product?.producer;
    if (!sellerId) return false;
    return sellerId.toString() === user._id;
  };

  const isHubProduct = hubIdFromUrl || product?.isHubDirect;
  const producerName = product?.producer?.farmName || product?.producer?.name;
  const producerAboutText = product?.producer?.aboutText || '';
  const producerSlug = product?.producer?.slug;

  if (loading) return <div className="text-center py-8">Loading product...</div>;
  if (error) return <div className="text-center py-8 text-red-600">Error: {error}</div>;
  if (!product) return <div className="text-center py-8">Product not found</div>;

  const imageUrl = product.images && product.images[0] ? `/uploads/${product.images[0]}` : '/placeholder.jpg';
  const availableQty = product.hubInventory !== undefined ? product.hubInventory : product.quantity;
  const isAvailable = product.available !== false && availableQty > 0;

  // Determine seller display
  const renderSellerInfo = () => {
    if (isHubProduct) {
      // Hub product: show producer name as plain text, with expandable info
      return (
        <div className="space-y-2">
          <p className="text-gray-600">
            Grown by: <span className="font-medium">{producerName || 'Local Farm'}</span>
          </p>
          {producerAboutText && (
            <div>
              <button 
                onClick={() => setShowGrowerInfo(!showGrowerInfo)}
                className="text-green-600 hover:text-green-800 text-sm underline"
              >
                {showGrowerInfo ? '− Show less' : '+ About the grower'}
              </button>
              {showGrowerInfo && (
                <div className="mt-2 p-3 bg-gray-50 rounded-lg text-sm text-gray-700">
                  <p className="italic">"{producerAboutText}"</p>
                  <p className="text-xs text-gray-500 mt-1 mt-2">
                    This product is grown and supplied by {producerName}.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }
    
    // Direct product: clickable link to producer storefront
    return (
      <p className="text-gray-600">
        From: <Link to={`/producer/${producerSlug}`} className="text-green-600 hover:underline">
          {producerName}
        </Link>
      </p>
    );
  };

  return (
    <div className="max-w-6xl mx-auto">
      <button onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-700 mb-4 inline-flex items-center gap-1">
        ← Back
      </button>

      <div className="grid md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg overflow-hidden">
          <img src={imageUrl} alt={product.name} className="w-full h-96 object-cover" />
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h1 className="text-3xl font-bold mb-2">{product.name}</h1>
          {product.organic && <span className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded mb-2 text-sm">Organic</span>}
          {product.isHubDirect && <span className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded mb-2 ml-2 text-sm">Hub Direct</span>}
          <p className="text-gray-600 mb-4">{product.description}</p>
          <div className="mb-4">
            <span className="text-2xl font-bold text-green-600">${product.price}/{product.unit}</span>
          </div>
          <div className="mb-4">
            <p className="text-gray-600">Available: {availableQty} {product.unit}</p>
          </div>
          
          {/* Seller/Grower information */}
          <div className="mb-4">
            {renderSellerInfo()}
          </div>
          
          {user && !isSeller() && isAvailable && (
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <label className="text-gray-600">Quantity:</label>
                <input
                  type="number"
                  min="1"
                  max={availableQty}
                  value={quantity}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    if (!isNaN(val) && val >= 1 && val <= availableQty) {
                      setQuantity(val);
                    }
                  }}
                  className="border rounded px-3 py-2 w-24"
                  disabled={!isAvailable}
                />
              </div>
              <button onClick={handleAddToCart} className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition">
                Add to Cart
              </button>
            </div>
          )}
          {!isAvailable && <p className="text-red-500 italic">Out of stock. Please check back later.</p>}
          {isSeller() && <p className="text-gray-500 italic">You cannot add your own product to cart.</p>}
        </div>
      </div>
    </div>
  );
}
